import { ref, Ref, reactive, UnwrapRef } from '@vue/composition-api';
import dayjs from 'dayjs';
import i18n, { i18n_t } from '@/locales/i18n';
import { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
type FileItemData = SMH.SpaceFileItemData;
export type IFormData = {
  title: string;
  describes: string;
  joinType: 0 | 1; // 0: 任何人 1: 指定成员
  validity: 0 | 1; // 0 自定义有效期 1 永久有效
  path: string;
  expireTime: string;
  allowedUsers?: CoFile.TeamOrMemberItem[];
  exampleFiles: FileItemData[];
  namingRules: string[];
  fileType: string[];
  spaceId?: string;
  spaceOrgId?: string;
};
export const initFormData = (): IFormData => {
  return {
    title: '',
    describes: '',
    joinType: 0,
    validity: 0,
    expireTime: dayjs().add(7, 'day').add(1, 'minute').format('YYYY-MM-DD HH:mm:ss'),
    path: '',
    allowedUsers: [],
    // 任何人则不传或空
    exampleFiles: [],
    namingRules: [],
    fileType: [],
    spaceId: '',
    spaceOrgId: '',
  };
};
type DialogVisible = {
  showAddFile: boolean;
  showAddFolder: boolean;
  showAddMembers: boolean;
};
interface Handler {
  formatOptions: Ref<
    Array<{
      [key: string]: string;
    }>
  >;
  namingRulesOptions: Ref<
    Array<{
      label: string;
      value: string;
    }>
  >;
  editOrCreateNamed: Ref<string>;
  newNamedOption: Ref<string>;
  dialogVisible: UnwrapRef<DialogVisible>;
  formRules: {};
  methods: {
    onAddNamedOption: () => void;
    onConfirmNamedOption: () => void;
    onCancelNamedOption: () => void;
    toggleDialogVisible: (type: 'showAddFile' | 'showAddFolder' | 'showAddMembers', flag: boolean) => void;
    initOtherData: () => void;
  };
}
const fileCollectFormDialogHandler = (): Handler => {
  const formatOptions = ref([
    {
      label: i18n_t('Word文档'),
      value: 'word',
    },
    {
      label: i18n_t('PPT文档'),
      value: 'ppt',
    },
    {
      label: i18n_t('Excel文档'),
      value: 'excel',
    },
    {
      label: i18n_t('PDF文档'),
      value: 'pdf',
    },
  ]);
  // 用函数保证每次初始化值
  const intNamingRulesOptions = (): Array<{
    label: string;
    value: string;
  }> => [
    {
      label: i18n_t('姓名'),
      value: 'name',
    },
    {
      label: i18n_t('手机号'),
      value: 'phoneNumber',
    },
    {
      label: i18n_t('学号'),
      value: 'studentId',
    },
    {
      label: i18n_t('工号'),
      value: 'workNumber',
    },
    {
      label: i18n_t('邮箱'),
      value: 'email',
    },
    {
      label: i18n_t('身份证号码'),
      value: 'idNumber',
    },
  ];
  const namingRulesOptions = ref<
    Array<{
      label: string;
      value: string;
    }>
  >(intNamingRulesOptions());
  const newNamedOption = ref('');
  const editOrCreateNamed = ref('create');
  const dialogVisible = reactive<DialogVisible>({
    showAddFile: false,
    showAddFolder: false,
    showAddMembers: false,
  });
  const validator = {
    title(name: string) {
      return new Promise(resolve => {
        const timer = setTimeout(() => {
          resolve(name.length <= 30);
          clearTimeout(timer);
        });
      });
    },
  };
  const formRules = {
    title: [
      {
        required: true,
        message: i18n_t('标题必填'),
        type: 'error',
        trigger: 'change',
      },
      {
        validator: validator.title,
        message: i18n_t('标题字数不得超过30字'),
      },
    ],
    namingRules: [
      {
        required: true,
        message: i18n_t('命名规则必填'),
        type: 'error',
        trigger: 'change',
      },
    ],
    path: [
      {
        required: true,
        message: i18n_t('文件路径不能为空'),
        type: 'error',
        trigger: 'change',
      },
    ],
  };
  const methods = {
    onAddNamedOption() {
      editOrCreateNamed.value = 'edit';
    },
    onConfirmNamedOption() {
      const flag = namingRulesOptions.value.some(item => item.label === newNamedOption.value);
      if (flag) {
        return tips.error(i18n_t('命名规则已存在'));
      }
      if (newNamedOption.value) {
        namingRulesOptions.value.push({
          label: newNamedOption.value.trim(),
          value: newNamedOption.value.trim(),
        });
        editOrCreateNamed.value = 'create';
        newNamedOption.value = '';
      }
    },
    onCancelNamedOption() {
      newNamedOption.value = '';
      editOrCreateNamed.value = 'create';
    },
    toggleDialogVisible(type: 'showAddFile' | 'showAddFolder' | 'showAddMembers', flag: boolean) {
      dialogVisible[type] = flag;
    },
    initOtherData() {
      namingRulesOptions.value = intNamingRulesOptions();
    },
  };
  return {
    formRules,
    dialogVisible,
    formatOptions,
    namingRulesOptions,
    editOrCreateNamed,
    newNamedOption,
    methods,
  };
};
export default fileCollectFormDialogHandler;
